export interface MotionState {
  position: number;
  velocity: number;
}

export interface Spring {
  stiffness: number;
  damping: number;
  mass: number;
  maxVelocity: number;
  restSpeed: number;
  restDistance: number;
  maxStepSeconds: number;
}

export interface ReleaseIntent {
  displacement: number;
  velocity: number;
  extent: number;
}

export interface Tilt {
  x: number;
  y: number;
}

export type CardTurnDirection = "reveal" | "conceal";

export interface CardTurnBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CardTurnPose {
  translateX: number;
  translateY: number;
  scaleX: number;
  scaleY: number;
  rotateY: number;
  lift: number;
}

export const CARD_SPRING: Readonly<Spring> = {
  stiffness: 390,
  damping: 34,
  mass: 1,
  maxVelocity: 3_200,
  restSpeed: 4,
  restDistance: 0.35,
  maxStepSeconds: 1 / 120,
};

export const CARD_TURN_SPRING: Readonly<Spring> = {
  stiffness: 260,
  damping: 27,
  mass: 1,
  maxVelocity: 6,
  restSpeed: 0.015,
  restDistance: 0.0015,
  maxStepSeconds: 1 / 120,
};

const maximumElapsedSeconds = 0.1;

function finite(value: number, label: string): number {
  if (!Number.isFinite(value)) {
    throw new Error(`${label} must be finite.`);
  }
  return value;
}

function validSpring(spring: Readonly<Spring>): void {
  for (const [label, value] of Object.entries(spring)) {
    finite(value, label);
    if (value <= 0) throw new Error(`${label} must be greater than zero.`);
  }
}

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(upper, Math.max(lower, value));
}

/**
 * Advances a damped spring with bounded semi-implicit integration.
 *
 * Capping and sub-stepping elapsed time keeps a backgrounded tab from
 * launching a card when animation frames resume.
 */
export function stepSpring(
  state: Readonly<MotionState>,
  target: number,
  elapsedSeconds: number,
  spring: Readonly<Spring> = CARD_SPRING,
): MotionState {
  validSpring(spring);
  let position = finite(state.position, "position");
  let velocity = finite(state.velocity, "velocity");
  const destination = finite(target, "target");
  const elapsed = clamp(
    finite(elapsedSeconds, "elapsedSeconds"),
    0,
    maximumElapsedSeconds,
  );
  if (elapsed === 0) return { position, velocity };

  const steps = Math.max(1, Math.ceil(elapsed / spring.maxStepSeconds));
  const step = elapsed / steps;
  for (let index = 0; index < steps; index += 1) {
    const springForce = -spring.stiffness * (position - destination);
    const dampingForce = -spring.damping * velocity;
    const acceleration = (springForce + dampingForce) / spring.mass;
    velocity = clamp(
      velocity + acceleration * step,
      -spring.maxVelocity,
      spring.maxVelocity,
    );
    position += velocity * step;
  }

  if (
    Math.abs(position - destination) <= spring.restDistance
    && Math.abs(velocity) <= spring.restSpeed
  ) {
    return { position: destination, velocity: 0 };
  }
  return { position, velocity };
}

export function springIsAtRest(
  state: Readonly<MotionState>,
  target: number,
  spring: Readonly<Spring> = CARD_SPRING,
): boolean {
  validSpring(spring);
  return (
    Math.abs(finite(state.position, "position") - finite(target, "target"))
      <= spring.restDistance
    && Math.abs(finite(state.velocity, "velocity")) <= spring.restSpeed
  );
}

/**
 * Keeps direct manipulation responsive near the pointer and increasingly
 * resistant near the edge. The result can never travel beyond one card extent.
 */
export function resistedDrag(displacement: number, extent: number): number {
  const distance = finite(displacement, "displacement");
  const cardExtent = finite(extent, "extent");
  if (cardExtent <= 0) throw new Error("extent must be greater than zero.");
  return Math.sign(distance)
    * cardExtent
    * (1 - Math.exp(-Math.abs(distance) / cardExtent));
}

/**
 * Returns screen direction (-1 left, 1 right), or zero for spring-back.
 * A release can move at most one card regardless of distance or speed.
 */
export function releaseDirection({
  displacement,
  velocity,
  extent,
}: Readonly<ReleaseIntent>): -1 | 0 | 1 {
  const distance = finite(displacement, "displacement");
  const speed = finite(velocity, "velocity");
  const cardExtent = finite(extent, "extent");
  if (cardExtent <= 0) throw new Error("extent must be greater than zero.");
  const distanceThreshold = Math.max(56, cardExtent * 0.18);
  const velocityThreshold = 620;
  const projected = distance + speed * 0.08;
  if (
    Math.abs(distance) < distanceThreshold
    && Math.abs(speed) < velocityThreshold
    && Math.abs(projected) < distanceThreshold
  ) {
    return 0;
  }
  const directionSource = Math.abs(speed) >= velocityThreshold
    ? speed
    : projected;
  return directionSource < 0 ? -1 : 1;
}

export function decayReleaseVelocity(
  velocity: number,
  idleSeconds: number,
  decayPerSecond = 14,
): number {
  const speed = finite(velocity, "velocity");
  const idle = finite(idleSeconds, "idleSeconds");
  const decay = finite(decayPerSecond, "decayPerSecond");
  if (idle < 0) throw new Error("idleSeconds must not be negative.");
  if (decay < 0) throw new Error("decayPerSecond must not be negative.");
  return speed * Math.exp(-idle * decay);
}

/**
 * Maps a spring's normalized progress onto one newly rendered card face.
 *
 * The new face starts at the prior face's exact viewport geometry, nearly
 * edge-on, then lifts and settles into its new layout slot. This lets callers
 * remove sensitive content before conceal motion while preserving a coherent
 * physical turn.
 */
export function cardTurnPose(
  progress: number,
  direction: CardTurnDirection,
  source: Readonly<CardTurnBounds>,
  target: Readonly<CardTurnBounds>,
): CardTurnPose {
  const normalized = clamp(finite(progress, "progress"), 0, 1);
  for (const [prefix, bounds] of [
    ["source", source],
    ["target", target],
  ] as const) {
    finite(bounds.left, `${prefix}.left`);
    finite(bounds.top, `${prefix}.top`);
    finite(bounds.width, `${prefix}.width`);
    finite(bounds.height, `${prefix}.height`);
    if (bounds.width <= 0 || bounds.height <= 0) {
      throw new Error(`${prefix} bounds must have positive dimensions.`);
    }
  }
  if (normalized === 1) {
    return {
      translateX: 0,
      translateY: 0,
      scaleX: 1,
      scaleY: 1,
      rotateY: 0,
      lift: 0,
    };
  }
  const remaining = 1 - normalized;
  const sourceCenterX = source.left + source.width / 2;
  const sourceCenterY = source.top + source.height / 2;
  const targetCenterX = target.left + target.width / 2;
  const targetCenterY = target.top + target.height / 2;
  const sourceScaleX = clamp(source.width / target.width, 0.42, 2.4);
  const sourceScaleY = clamp(source.height / target.height, 0.42, 2.4);
  const turnDirection = direction === "reveal" ? -1 : 1;
  return {
    translateX: (sourceCenterX - targetCenterX) * remaining,
    translateY: (sourceCenterY - targetCenterY) * remaining,
    scaleX: 1 + (sourceScaleX - 1) * remaining,
    scaleY: 1 + (sourceScaleY - 1) * remaining,
    rotateY: turnDirection * 86 * remaining,
    lift: Math.sin(normalized * Math.PI) * 28,
  };
}

export function pointerTilt(
  clientX: number,
  clientY: number,
  bounds: Pick<DOMRectReadOnly, "left" | "top" | "width" | "height">,
  maximumDegrees = 5,
): Tilt {
  const width = finite(bounds.width, "width");
  const height = finite(bounds.height, "height");
  const maximum = finite(maximumDegrees, "maximumDegrees");
  if (width <= 0 || height <= 0 || maximum < 0) {
    throw new Error("Pointer tilt needs positive bounds and a nonnegative limit.");
  }
  const normalizedX = clamp(
    (finite(clientX, "clientX") - finite(bounds.left, "left")) / width,
    0,
    1,
  );
  const normalizedY = clamp(
    (finite(clientY, "clientY") - finite(bounds.top, "top")) / height,
    0,
    1,
  );
  return {
    x: (0.5 - normalizedY) * maximum * 2,
    y: (normalizedX - 0.5) * maximum * 2,
  };
}
